import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Globe, User, FileText, Users, ChevronDown, Calendar, Check, Send, X, ChevronLeft, ChevronRight, TrendingUp, CreditCard, Building2, Gauge, ShieldCheck, Briefcase, CalendarDays, Tag, Bell, UserCheck, ArrowRight, Rocket, FileEdit } from 'lucide-react';
import { can } from '@/utils/perms';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';
import { showAlert } from '@/components/AppDialog';
import { BRAND } from '@/brand';
import { getAllUsers } from '@/utils/adminUsers';
const IS_CORP = BRAND === 'corporate';
// Brand accent for the dashboard — amber on the accounting/corporate domain, blue on consulting.
const AX = IS_CORP
  ? { hero: 'from-amber-400 via-amber-500 to-amber-600', iconBox: 'bg-amber-500', solid: 'bg-amber-500 text-white shadow-md', solidBtn: 'bg-amber-500 hover:bg-amber-600', text: 'text-amber-700', textBtn: 'text-amber-700 hover:bg-amber-50', avatar: 'from-amber-400 to-amber-600', accent: 'accent-amber-600', softBar: 'bg-amber-50 border-amber-100', softBarText: 'text-amber-800', selectedRow: 'bg-amber-50/50', tooltip: '#B45309' }
  : { hero: 'from-blue-400 via-blue-500 to-blue-600', iconBox: 'bg-blue-600', solid: 'bg-blue-600 text-white shadow-md', solidBtn: 'bg-blue-600 hover:bg-blue-700', text: 'text-blue-600', textBtn: 'text-blue-600 hover:bg-blue-50', avatar: 'from-blue-500 to-blue-600', accent: 'accent-blue-600', softBar: 'bg-blue-50 border-blue-100', softBarText: 'text-blue-800', selectedRow: 'bg-blue-50/40', tooltip: '#2563EB' };
// Overview reskin palette (visual only) — brand-scoped accent for the scoped .adminx <style> block.
import AdminSidebar from '../components/AdminSidebar';
import NewClientButton from '../components/NewClientButton';
import { type Kpi } from '../components/AdminStatHero';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CountryFilter from '../components/CountryFilter';
import AdminProfileMenu from '../components/AdminProfileMenu';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';

type UserData = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country?: string;
  onboardingCompleted: boolean;
  goalAssessment?: any;
  investorReadinessForm1?: any;
  investorReadinessForm2?: any;
  irScore?: {
    totalScore: number;
    category: string;
  };
  capitalAssessment?: {
    stage?: string;
    raiseAmount?: string;
    fundingType?: string;
    fundraisingStage?: string;
    challenge?: string;
    completedAt?: string;
  };
  businessSetupData?: {
    type?: 'incorporate' | 'operate';
    planName?: string;
    total?: number;
    submittedAt?: string;
  };
  createdAt: string;
  paymentStatus?: string;
  lastLoginAt?: string;
  brand?: 'corporate' | 'consulting' | null;
};

function mapToR(score: number, t: any) {
  if (score <= 33) return { code: 'R1', label: t('admin.risk.critical'), range: '0–33' };
  if (score <= 41) return { code: 'R2', label: t('admin.risk.veryHigh'), range: '34–41' };
  if (score <= 48) return { code: 'R3', label: t('admin.risk.high'), range: '42–48' };
  if (score <= 56) return { code: 'R4', label: t('admin.risk.medium'), range: '49–56' };
  if (score <= 65) return { code: 'R5', label: t('admin.risk.semiReady'), range: '57–65' };
  if (score <= 74) return { code: 'R6', label: t('admin.risk.investorReady'), range: '66–74' };
  return { code: 'R7', label: t('admin.risk.exceptional'), range: '75–100' };
}

type FilterState = {
  country: string;
  startDate: string;
  endDate: string;
  product: string;
  score: string;
  result: string;
};



export default function Admin() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');

  const PRODUCT_OPTIONS = useMemo(() => [
    { value: 'raising', label: t('admin.product.raising') },
    { value: 'consulting', label: t('admin.product.consulting') }
  ], [t]);

  const SCORE_OPTIONS = useMemo(() => [
    { value: 'Yes', label: t('admin.score.yes') },
    { value: 'No', label: t('admin.score.no') }
  ], [t]);

  const G_OPTIONS = useMemo(() => [
    { value: 'G1', label: `G1 (${t('dashboard.capitalReadiness')})` },
    { value: 'G2', label: `G2 (${t('dashboard.growthArchitecture')})` },
    { value: 'G3', label: `G3 (${t('dashboard.growthFinancial')})` },
    { value: 'G4', label: `G4 (${t('dashboard.governance')})` }
  ], [t]);

  const R_OPTIONS = useMemo(() => [
    { value: 'R1', label: `R1 (${t('admin.risk.critical')})` },
    { value: 'R2', label: `R2 (${t('admin.risk.veryHigh')})` },
    { value: 'R3', label: `R3 (${t('admin.risk.high')})` },
    { value: 'R4', label: `R4 (${t('admin.risk.medium')})` },
    { value: 'R5', label: `R5 (${t('admin.risk.semiReady')})` },
    { value: 'R6', label: `R6 (${t('admin.risk.investorReady')})` },
    { value: 'R7', label: `R7 (${t('admin.risk.exceptional')})` }
  ], [t]);

  const RESULT_COMBINATIONS = useMemo(() => G_OPTIONS.flatMap(g =>
    R_OPTIONS.map(r => ({
      value: `${g.value}_${r.value}`,
      label: `${g.value}.${r.value}`
    }))
  ), [G_OPTIONS, R_OPTIONS]);

  const [users, setUsers] = useState<UserData[]>([]);

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Brand (domain) split: default the admin view to the current domain's brand.
  const [brandView, setBrandView] = useState<'corporate' | 'consulting' | 'all'>(BRAND);
  const [adminName, setAdminName] = useState('Admin');

  const [filters, setFilters] = useState<FilterState>({
    country: '',
    startDate: '',
    endDate: '',
    product: '',
    score: '',
    result: ''
  });

  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  // Applied filters state (what actually filters the list)
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({
    country: '',
    startDate: '',
    endDate: '',
    product: '',
    score: '',
    result: ''
  });
  const [appliedActiveFilters, setAppliedActiveFilters] = useState<string[]>([]);

  // Pagination + bulk selection
  const pageSize = 25;
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTitle, setBulkTitle] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    // Get admin email from localStorage
    const adminUserStr = localStorage.getItem('adminUser');
    if (adminUserStr) {
      try {
        const adminUser = JSON.parse(adminUserStr);
        if (adminUser.email) {
          setAdminName(adminUser.email);
        }
      } catch (err) {
        console.error('Failed to parse adminUser', err);
      }
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await getAllUsers();          // shared session cache — fetched once
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    // --- FILTER LOGIC ---
    let result = [...users];

    // 1. Search Query
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(user =>
        (user.email || '').toLowerCase().includes(lowerQuery) ||
        (user.firstName || '').toLowerCase().includes(lowerQuery) ||
        (user.lastName || '').toLowerCase().includes(lowerQuery) ||
        (user.phone || '').includes(searchQuery)
      );
    }

    // 2. Quick Filters (Additive)
    if (appliedActiveFilters.includes('newClients')) {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      result = result.filter(user => {
        if (!user.createdAt) return false;
        const created = new Date(user.createdAt);
        return created >= thirtyDaysAgo;
      });
    }

    if (appliedActiveFilters.includes('paidClients')) {
      // paymentStatus must be 'paid'
      result = result.filter(user => user.paymentStatus === 'paid');
    }

    if (appliedActiveFilters.includes('nonPaidClients')) {
      // paymentStatus must NOT be 'paid' (could be 'unpaid', undefined, or null)
      result = result.filter(user => user.paymentStatus !== 'paid');
    }

    if (appliedActiveFilters.includes('completedKYC')) {
      // Has IR Score
      result = result.filter(user => !!user.irScore);
    }

    if (appliedActiveFilters.includes('notCompletedKYC')) {
      result = result.filter(user => !user.irScore);
    }

    if (appliedActiveFilters.includes('hasCapitalAssessment')) {
      // capitalReadiness is the field actually written by the assessment (capitalAssessment is a dead field).
      result = result.filter(user => !!user.capitalReadiness);
    }

    if (appliedActiveFilters.includes('noCapitalAssessment')) {
      result = result.filter(user => !user.capitalReadiness);
    }

    if (appliedActiveFilters.includes('hasBusinessSetup')) {
      result = result.filter(user => !!user.businessSetupData?.submittedAt);
    }

    // 3. Advanced Filters (Additive)

    // Country
    // Brand (domain) split: legacy/untagged accounts count as consulting (original site).
    if (brandView !== 'all') {
      result = result.filter(user =>
        brandView === 'consulting' ? (user.brand === 'consulting' || !user.brand) : user.brand === brandView
      );
    }

    if (appliedFilters.country) {
      const filterCountry = appliedFilters.country.toLowerCase();
      result = result.filter(user =>
        (user.country || '').toLowerCase().includes(filterCountry)
      );
    }

    // Date Range: Start
    if (appliedFilters.startDate) {
      const start = new Date(appliedFilters.startDate);
      // Reset time to start of day 00:00:00
      start.setHours(0, 0, 0, 0);

      result = result.filter(user => {
        if (!user.createdAt) return false;
        const created = new Date(user.createdAt);
        return created >= start;
      });
    }

    // Date Range: End
    if (appliedFilters.endDate) {
      const end = new Date(appliedFilters.endDate);
      // Set time to end of day 23:59:59
      end.setHours(23, 59, 59, 999);

      result = result.filter(user => {
        if (!user.createdAt) return false;
        const created = new Date(user.createdAt);
        return created <= end;
      });
    }

    // Product
    if (appliedFilters.product) {
      const filterProduct = appliedFilters.product.toLowerCase();
      result = result.filter(user => {
        const hasRaising = !!(user as any).raiseCapitalForm && Object.keys((user as any).raiseCapitalForm || {}).length > 0;
        // Construct a searchable string of products for this user
        const userProducts = ['consulting'];
        if (hasRaising) userProducts.push('raising');
        return userProducts.join(' ').toLowerCase().includes(filterProduct);
      });
    }

    // Score (Yes/No)
    if (appliedFilters.score) {
      const filterScore = appliedFilters.score.toLowerCase(); // 'yes' or 'no'
      result = result.filter(user => {
        const hasScore = !!user.irScore;
        return (hasScore ? 'yes' : 'no') === filterScore;
      });
    }

    // Result (G_R Combination)
    if (appliedFilters.result) {
      const [gVal, rVal] = appliedFilters.result.split('_');
      result = result.filter(user => {
        // Must match G-Score logic (Goal Assessment Group)
        const userG = user.goalAssessment?.group;
        if (userG !== gVal) return false;

        // Must match R-Score logic
        if (!user.irScore?.totalScore) return false;
        const rDetails = mapToR(user.irScore.totalScore, t);
        return rDetails.code === rVal;
      });
    }

    // Sort by createdAt descending (newest first) by default
    result.sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA;
    });

    return result;
  }, [users, searchQuery, appliedFilters, appliedActiveFilters, brandView]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(
    () => filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredUsers, currentPage]
  );

  // Reset page + selection whenever the filtered set changes.
  useEffect(() => { setPage(1); setSelected(new Set()); }, [searchQuery, appliedFilters, appliedActiveFilters, brandView, users.length]);

  // Bulk selection helpers
  const toggleOne = (id: string) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const allPageSelected = pagedUsers.length > 0 && pagedUsers.every(u => selected.has(u._id));
  const toggleAllPage = () => setSelected(prev => {
    const n = new Set(prev);
    if (allPageSelected) pagedUsers.forEach(u => n.delete(u._id));
    else pagedUsers.forEach(u => n.add(u._id));
    return n;
  });

  const sendBulkNotification = async () => {
    if (!bulkTitle.trim() || !bulkMsg.trim()) { showAlert('Add a title and a message.'); return; }
    const uids = Array.from(selected);
    if (!uids.length) return;
    setBulkBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/auth/send-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ uids, notification: { title: bulkTitle, message: bulkMsg, type: 'message', priority: 'NORMAL' } }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || (json && json.success === false)) throw new Error(json?.error || 'Send failed');
      const count = uids.length;
      setBulkOpen(false); setBulkTitle(''); setBulkMsg(''); setSelected(new Set());
      showAlert(`Notification sent to ${count} client${count !== 1 ? 's' : ''}.`);
    } catch (e: any) {
      showAlert(e.message || 'Failed to send notification.');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    navigate('/admin-login');
  };

  // KPIs are scoped to the current brand view (same rule as the client list and
  // /admin/analytics) so the hero counts match Analytics instead of counting all brands.
  const brandScoped = users.filter(u =>
    brandView === 'all' ? true
      : brandView === 'consulting' ? (u.brand === 'consulting' || !u.brand)
      : u.brand === brandView
  );

  const getNewClientsCount = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return brandScoped.filter(user => new Date(user.createdAt) >= thirtyDaysAgo).length;
  };

  const getTotalClients = () => {
    return brandScoped.length;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const resetFilters = () => {
    setFilters({
      country: '',
      startDate: '',
      endDate: '',
      product: '',
      score: '',
      result: ''
    });
    setActiveFilters([]);
    setAppliedFilters({
      country: '',
      startDate: '',
      endDate: '',
      product: '',
      score: '',
      result: ''
    });
    setAppliedActiveFilters([]);
  };

  const toggleFilter = (filterKey: string) => {
    setActiveFilters(prev => {
      // Check if filter is already active
      const isActive = prev.includes(filterKey);

      if (isActive) {
        // If already active, remove it
        return prev.filter(f => f !== filterKey);
      } else {
        // If not active, add it and remove mutually exclusive filters
        let newFilters = [...prev, filterKey];

        // Handle Paid/Non-Paid mutual exclusivity
        if (filterKey === 'paidClients') {
          newFilters = newFilters.filter(f => f !== 'nonPaidClients');
        } else if (filterKey === 'nonPaidClients') {
          newFilters = newFilters.filter(f => f !== 'paidClients');
        }

        // Handle KYC Completed/Not Completed mutual exclusivity
        if (filterKey === 'completedKYC') {
          newFilters = newFilters.filter(f => f !== 'notCompletedKYC');
        } else if (filterKey === 'notCompletedKYC') {
          newFilters = newFilters.filter(f => f !== 'completedKYC');
        }

        // Handle Capital Assessment mutual exclusivity
        if (filterKey === 'hasCapitalAssessment') {
          newFilters = newFilters.filter(f => f !== 'noCapitalAssessment');
        } else if (filterKey === 'noCapitalAssessment') {
          newFilters = newFilters.filter(f => f !== 'hasCapitalAssessment');
        }

        return newFilters;
      }
    });
  };

  // --- Chart Data Preparation ---
  const getChartData = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    // Use brandScoped (not all users) so the curve matches the "+N" label and the "New this month" KPI.
    const newUsers = brandScoped.filter(user => new Date(user.createdAt) >= thirtyDaysAgo);

    const last30Days: string[] = [];
    for (let i = 30; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateString = d.toISOString().split('T')[0];
      if (dateString && !last30Days.includes(dateString)) {
        last30Days.push(dateString);
      }
    }

    const usersByDate: Record<string, number> = {};
    newUsers.forEach(user => {
      if (user.createdAt) {
        try {
          const date = new Date(user.createdAt);
          if (!isNaN(date.getTime())) {
            const dateKey = date.toISOString().split('T')[0];
            if (dateKey) {
              usersByDate[dateKey] = (usersByDate[dateKey] || 0) + 1;
            }
          }
        } catch (e) {
          // Ignore invalid dates
        }
      }
    });

    let cumulativeCount = 0;
    return last30Days.map(date => {
      const count = usersByDate[date] || 0;
      cumulativeCount += count;
      return {
        date,
        value: cumulativeCount
      };
    });
  };

  const chartData = getChartData();

  const paidCount = brandScoped.filter(u => (u as any).paymentStatus === 'paid').length;
  const ordersCount = brandScoped.filter(u => (u as any).businessSetupData).length;
  // "Assessments done" = completed Capital Readiness assessments — the same field
  // (capitalReadiness) the Analytics funnel + KPI use, so the numbers match.
  const assessmentsCount = brandScoped.filter(u => (u as any).capitalReadiness).length;
  const adminFirst = (adminName.split('@')[0] || 'Admin').replace(/^\w/, c => c.toUpperCase());
  const kpis: Kpi[] = [
    { label: 'Total clients', value: getTotalClients(), icon: <Users className="w-5 h-5" /> },
    { label: 'New this month', value: getNewClientsCount(), icon: <TrendingUp className="w-5 h-5" /> },
    { label: 'Paid clients', value: paidCount, icon: <CreditCard className="w-5 h-5" /> },
    IS_CORP
      ? { label: 'Setup orders', value: ordersCount, icon: <Building2 className="w-5 h-5" /> }
      : { label: 'Assessments done', value: assessmentsCount, icon: <Gauge className="w-5 h-5" /> },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">{t('dashboard.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <AdminSidebar />

      {/* Main Content */}
      <main className={`flex-1 w-full overflow-x-hidden ${isRTL ? 'xl:mr-64' : 'xl:ml-64'}`}>
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-4 md:py-5 flex items-center justify-between sticky top-0 z-30 shadow-sm">
          <div className={`flex-1 max-w-3xl ${isRTL ? 'xl:mr-0 mr-10' : 'xl:ml-0 ml-10'}`}>

            <div className="relative">
              <Search className={`absolute ${isRTL ? 'left-3 md:left-4' : 'left-3 md:left-4'} top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 md:w-5 md:h-5`} />
              <input
                type="text"
                placeholder={t('admin.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full ${isRTL ? 'pl-10 md:pl-12 pr-3 md:pr-4' : 'pl-10 md:pl-12 pr-3 md:pr-4'} py-2.5 md:py-3 border border-slate-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-slate-50 hover:bg-white transition-colors text-black ${isRTL ? 'text-right' : 'text-left'}`}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 ml-2 md:ml-6">
            <NewClientButton />
            <AdminProfileMenu />
          </div>
        </header>

        {/* Dashboard Content — Overview reskin (visual only) */}
        <div className="adminx p-4 md:p-6 max-w-[1600px] mx-auto">
          <style>{`
            .adminx {
              font-family: 'Poppins', system-ui, sans-serif;
              --ax-accent: var(--brand);
              --ax-accent-strong: var(--brand-d);
              --ax-accent-soft: var(--brand-soft);
              --ax-ink: #0F172A;
              --ax-muted: #64748B;
              --ax-line: #E6E9ED;
              color: var(--ax-ink);
            }

            /* Welcome banner */
            .adminx .ax-banner {
              position: relative;
              overflow: hidden;
              border-radius: 16px;
              padding: 26px 28px;
              margin-bottom: 18px;
              background: linear-gradient(135deg, var(--ax-accent), var(--ax-accent-strong));
              color: #fff;
              box-shadow: 0 12px 30px -14px rgba(15,23,42,.4);
            }
            .adminx .ax-banner::after {
              content: "";
              position: absolute;
              inset-inline-end: -60px;
              top: -70px;
              width: 240px;
              height: 240px;
              background: radial-gradient(circle, rgba(255,255,255,.18), transparent 70%);
              pointer-events: none;
            }
            .adminx .ax-eyebrow {
              position: relative;
              text-transform: uppercase;
              letter-spacing: .12em;
              font-size: 11px;
              font-weight: 700;
              opacity: .88;
              margin: 0 0 6px;
            }
            .adminx .ax-banner-title {
              position: relative;
              font-size: 24px;
              font-weight: 700;
              line-height: 1.15;
              margin: 0 0 6px;
            }
            .adminx .ax-banner-sub {
              position: relative;
              font-size: 13.5px;
              font-weight: 500;
              opacity: .92;
              margin: 0;
            }

            /* Stat cards */
            .adminx .ax-stats {
              display: grid;
              grid-template-columns: repeat(2, minmax(0,1fr));
              gap: 12px;
              margin-bottom: 18px;
            }
            @media (min-width: 1024px) { .adminx .ax-stats { grid-template-columns: repeat(4, minmax(0,1fr)); } }
            .adminx .ax-stat {
              display: flex;
              align-items: center;
              gap: 12px;
              background: #fff;
              border: 1px solid var(--ax-line);
              border-radius: 14px;
              padding: 16px;
              box-shadow: 0 1px 2px rgba(15,23,42,.05);
            }
            .adminx .ax-stat-icon {
              flex: 0 0 auto;
              width: 34px;
              height: 34px;
              border-radius: 10px;
              display: grid;
              place-items: center;
              background: var(--ax-accent-soft);
              color: var(--ax-accent);
            }
            .adminx .ax-stat-value {
              font-size: 25px;
              font-weight: 700;
              line-height: 1;
              margin: 0;
              color: var(--ax-ink);
              font-variant-numeric: tabular-nums;
            }
            .adminx .ax-stat-label {
              font-size: 12px;
              font-weight: 500;
              color: var(--ax-muted);
              margin: 5px 0 0;
            }

            /* Quick actions */
            .adminx .ax-actions {
              display: grid;
              grid-template-columns: repeat(2, minmax(0,1fr));
              gap: 12px;
              margin-bottom: 18px;
            }
            @media (min-width: 768px) { .adminx .ax-actions { grid-template-columns: repeat(3, minmax(0,1fr)); } }
            @media (min-width: 1280px) { .adminx .ax-actions { grid-template-columns: repeat(5, minmax(0,1fr)); } }
            .adminx .ax-action {
              text-align: start;
              background: #fff;
              border: 1px solid var(--ax-line);
              border-radius: 14px;
              padding: 16px;
              box-shadow: 0 1px 2px rgba(15,23,42,.05);
              cursor: pointer;
              transition: box-shadow .15s ease, border-color .15s ease, transform .15s ease;
            }
            .adminx .ax-action:hover {
              box-shadow: 0 10px 26px -16px rgba(15,23,42,.4);
              border-color: var(--ax-accent);
              transform: translateY(-1px);
            }
            .adminx .ax-action-icon {
              width: 38px;
              height: 38px;
              border-radius: 11px;
              display: grid;
              place-items: center;
              background: var(--ax-accent-soft);
              color: var(--ax-accent);
              margin-bottom: 12px;
            }
            .adminx .ax-action-label {
              font-size: 13.5px;
              font-weight: 700;
              color: var(--ax-ink);
              line-height: 1.2;
              margin: 0;
            }
            .adminx .ax-action-sub {
              display: flex;
              align-items: center;
              gap: 4px;
              font-size: 11px;
              color: var(--ax-muted);
              margin: 5px 0 0;
            }
            .adminx .ax-action-arrow {
              color: var(--ax-accent);
              opacity: 0;
              transform: translateX(-4px);
              transition: opacity .15s ease, transform .15s ease;
            }
            .adminx .ax-action:hover .ax-action-arrow { opacity: 1; transform: translateX(0); }

            /* Generic card */
            .adminx .ax-card {
              background: #fff;
              border: 1px solid var(--ax-line);
              border-radius: 14px;
              box-shadow: 0 1px 2px rgba(15,23,42,.05);
              margin-bottom: 18px;
            }
            .adminx .ax-card-pad { padding: 20px; }
            .adminx .ax-card-head {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              margin-bottom: 16px;
            }
            .adminx .ax-card-title { font-size: 15px; font-weight: 700; color: var(--ax-ink); margin: 0; }
            .adminx .ax-card-desc { font-size: 12px; color: var(--ax-muted); margin: 2px 0 0; }
            .adminx .ax-bignum { font-size: 22px; font-weight: 800; color: var(--ax-ink); font-variant-numeric: tabular-nums; }

            /* Filters */
            .adminx .ax-filter-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 12px;
              margin-bottom: 18px;
            }
            @media (min-width: 640px) { .adminx .ax-filter-grid { grid-template-columns: repeat(2, minmax(0,1fr)); } }
            @media (min-width: 1024px) { .adminx .ax-filter-grid { grid-template-columns: repeat(3, minmax(0,1fr)); } }
            @media (min-width: 1280px) { .adminx .ax-filter-grid { grid-template-columns: repeat(6, minmax(0,1fr)); } }
            .adminx .ax-filters-title { font-size: 13px; font-weight: 700; color: #334155; margin: 0 0 10px; }
            .adminx .ax-chips { display: flex; flex-wrap: wrap; gap: 8px; align-items: stretch; }

            /* Filter chips */
            .adminx .ax-chip {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 8px 16px;
              border-radius: 999px;
              font-size: 13px;
              font-weight: 600;
              color: #475569;
              background: #fff;
              border: 1px solid var(--ax-line);
              cursor: pointer;
              transition: background .15s ease, color .15s ease, border-color .15s ease;
            }
            .adminx .ax-chip::before {
              content: "";
              width: 6px;
              height: 6px;
              border-radius: 999px;
              background: #CBD5E1;
            }
            .adminx .ax-chip:hover { background: #F8FAFC; }
            .adminx .ax-chip.is-active {
              background: #0F172A;
              color: #fff;
              border-color: #0F172A;
            }
            .adminx .ax-chip.is-active::before { background: #fff; }

            .adminx .ax-btn-primary {
              padding: 9px 22px;
              border-radius: 10px;
              font-size: 13px;
              font-weight: 700;
              color: #fff;
              background: var(--ax-accent);
              border: none;
              cursor: pointer;
              box-shadow: 0 1px 2px rgba(15,23,42,.08);
              transition: background .15s ease;
            }
            .adminx .ax-btn-primary:hover { background: var(--ax-accent-strong); }
            .adminx .ax-ml-auto { margin-inline-start: auto; }

            /* Table card */
            .adminx .ax-table-card {
              background: #fff;
              border: 1px solid var(--ax-line);
              border-radius: 14px;
              box-shadow: 0 1px 2px rgba(15,23,42,.05);
              overflow: hidden;
            }
            .adminx .ax-table-head {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              padding: 18px 22px;
              border-bottom: 1px solid var(--ax-line);
            }
            .adminx .ax-table-title { font-size: 17px; font-weight: 700; color: var(--ax-ink); margin: 0; }
            .adminx .ax-table-count { font-size: 12px; font-weight: 600; color: #94A3B8; }

            /* Bulk bar */
            .adminx .ax-bulk {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              padding: 12px 22px;
              border-bottom: 1px solid var(--ax-line);
              background: var(--ax-accent-soft);
            }
            .adminx .ax-bulk-count { font-size: 13px; font-weight: 700; color: var(--ax-accent-strong); }
            .adminx .ax-bulk-btn {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 7px 14px;
              border-radius: 8px;
              font-size: 12px;
              font-weight: 700;
              color: #fff;
              background: var(--ax-accent);
              border: none;
              cursor: pointer;
              transition: background .15s ease;
            }
            .adminx .ax-bulk-btn:hover { background: var(--ax-accent-strong); }
            .adminx .ax-ghost-btn {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 7px 14px;
              border-radius: 8px;
              font-size: 12px;
              font-weight: 700;
              color: #64748B;
              background: #fff;
              border: 1px solid var(--ax-line);
              cursor: pointer;
            }
            .adminx .ax-ghost-btn:hover { background: #F8FAFC; }

            /* Table */
            .adminx .ax-table { width: 100%; border-collapse: collapse; }
            .adminx .ax-th {
              padding: 12px 24px;
              font-size: 11px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: .06em;
              color: #64748B;
              background: #FBFCFD;
              border-bottom: 1px solid var(--ax-line);
              white-space: nowrap;
            }
            .adminx .ax-td {
              padding: 14px 24px;
              font-size: 13px;
              color: #334155;
              border-bottom: 1px solid #F1F5F9;
              vertical-align: middle;
            }
            .adminx .ax-row { transition: background .12s ease; }
            .adminx .ax-row:hover { background: #FCFDFE; }
            .adminx .ax-row.is-selected { background: var(--ax-accent-soft); }
            .adminx .ax-row:last-child .ax-td { border-bottom: none; }
            .adminx .ax-strong { font-size: 13px; font-weight: 600; color: var(--ax-ink); margin: 0; }

            /* Avatar */
            .adminx .ax-avatar {
              flex: 0 0 auto;
              width: 44px;
              height: 44px;
              border-radius: 14px;
              display: grid;
              place-items: center;
              color: #fff;
              font-size: 15px;
              font-weight: 700;
              background: linear-gradient(135deg, var(--ax-accent), var(--ax-accent-strong));
              box-shadow: 0 1px 2px rgba(15,23,42,.14);
            }
            .adminx .ax-avatar-sm { width: 40px; height: 40px; border-radius: 12px; font-size: 14px; }
            .adminx .ax-name { font-size: 13px; font-weight: 600; color: var(--ax-ink); margin: 0; }
            .adminx .ax-sub { font-size: 12px; color: var(--ax-muted); margin: 2px 0 0; }

            /* Pills */
            .adminx .ax-pill {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 4px 12px;
              border-radius: 999px;
              font-size: 11.5px;
              font-weight: 700;
              line-height: 1;
            }
            .adminx .ax-pill::before {
              content: "";
              width: 6px;
              height: 6px;
              border-radius: 999px;
              background: currentColor;
              opacity: .85;
            }
            .adminx .ax-pill-corp { background: #FDEEDD; color: #C76B12; }
            .adminx .ax-pill-consult { background: #E8F0FE; color: #1D4ED8; }
            .adminx .ax-pill-raise { background: #FFEDD5; color: #C2410C; }
            .adminx .ax-pill-done { background: #DCFCE7; color: #15803D; }
            .adminx .ax-pill-none { background: #F1F5F9; color: #94A3B8; }
            .adminx .ax-pill-none::before { display: none; }

            /* View button */
            .adminx .ax-view-btn {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 8px 16px;
              border-radius: 10px;
              font-size: 13px;
              font-weight: 600;
              color: var(--ax-accent-strong);
              background: transparent;
              border: none;
              cursor: pointer;
              transition: background .12s ease;
            }
            .adminx .ax-view-btn:hover { background: var(--ax-accent-soft); }

            /* Mobile rows */
            .adminx .ax-mrow { padding: 16px; border-bottom: 1px solid #F1F5F9; transition: background .12s ease; }
            .adminx .ax-mrow:last-child { border-bottom: none; }
            .adminx .ax-mrow:hover { background: #FCFDFE; }
            .adminx .ax-mlink { font-size: 12px; font-weight: 600; color: var(--ax-accent-strong); background: none; border: none; cursor: pointer; }
            .adminx .ax-kv { display: flex; align-items: center; justify-content: space-between; }
            .adminx .ax-kv-k { font-size: 12px; color: var(--ax-muted); }
            .adminx .ax-kv-v { font-size: 12px; font-weight: 500; color: var(--ax-ink); }

            /* Empty state */
            .adminx .ax-empty { text-align: center; padding: 64px 20px; }
            .adminx .ax-empty-icon {
              width: 72px;
              height: 72px;
              border-radius: 18px;
              background: #F1F5F9;
              color: #94A3B8;
              display: grid;
              place-items: center;
              margin: 0 auto 14px;
            }

            /* Pagination */
            .adminx .ax-pager {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              padding: 14px 22px;
              border-top: 1px solid var(--ax-line);
            }
            .adminx .ax-pager-info { font-size: 12px; font-weight: 500; color: var(--ax-muted); }
            .adminx .ax-pager-btns { display: flex; align-items: center; gap: 6px; }
            .adminx .ax-pager-btn {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 7px 12px;
              border-radius: 8px;
              font-size: 13px;
              font-weight: 600;
              color: #475569;
              background: #fff;
              border: 1px solid var(--ax-line);
              cursor: pointer;
            }
            .adminx .ax-pager-btn:hover:not(:disabled) { background: #F8FAFC; }
            .adminx .ax-pager-btn:disabled { opacity: .4; cursor: not-allowed; }
            .adminx .ax-pager-page { font-size: 12px; font-weight: 700; color: var(--ax-muted); padding: 0 8px; }
          `}</style>

          {/* Welcome banner + KPI stat cards (renders the same kpis numbers/labels) */}
          <div className="ax-banner">
            <p className="ax-eyebrow">{IS_CORP ? 'Accounting & Corporate · Admin' : 'Capital Readiness · Admin'}</p>
            <h1 className="ax-banner-title">Welcome back, {adminFirst}</h1>
            <p className="ax-banner-sub">
              {`${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} — ${getTotalClients()} clients in your book, ${getNewClientsCount()} new this month.`}
            </p>
          </div>

          <div className="ax-stats">
            {kpis.map((k) => (
              <div key={k.label} className="ax-stat">
                <span className="ax-stat-icon">{k.icon}</span>
                <div>
                  <p className="ax-stat-value">{k.value}</p>
                  <p className="ax-stat-label">{k.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick actions — jump to the system areas */}
          {(() => {
            const tiles = IS_CORP ? [
              { section: 'services', label: 'Services & Orders', sub: 'Fulfil setup orders', icon: Briefcase, path: '/admin/services' },
              { section: 'compliance', label: 'Compliance Calendar', sub: 'Deadlines & filings', icon: CalendarDays, path: '/admin/compliance-calendar' },
              { section: 'pricing', label: 'Accounting Pricing', sub: 'Fees, bands & packages', icon: Tag, path: '/admin/pricing' },
              { section: 'leads', label: 'Leads', sub: 'Demo & enquiries', icon: UserCheck, path: '/admin/leads' },
              { section: 'notifications', label: 'Notifications', sub: 'Broadcast & alerts', icon: Bell, path: '/admin/notifications' },
            ] : [
              { section: 'raising', label: 'Raising Requests', sub: 'Capital pipeline', icon: Users, path: '/raising-request' },
              { section: 'pricing', label: 'Consulting Pricing', sub: 'Plans & tiers', icon: Rocket, path: '/admin/consulting-pricing' },
              { section: 'content', label: 'Content Studio', sub: 'Reports & assessments', icon: FileEdit, path: '/admin/content-studio' },
              { section: 'leads', label: 'Leads', sub: 'Demo & enquiries', icon: UserCheck, path: '/admin/leads' },
              { section: 'notifications', label: 'Notifications', sub: 'Broadcast & alerts', icon: Bell, path: '/admin/notifications' },
            ];
            const shown = tiles.filter(t => can(t.section));
            if (!shown.length) return null;
            return (
              <div className="ax-actions">
                {shown.map(t => (
                  <button
                    key={t.label}
                    onClick={() => navigate(t.path)}
                    className="ax-action"
                  >
                    <span className="ax-action-icon">
                      <t.icon className="w-5 h-5" />
                    </span>
                    <p className="ax-action-label">{t.label}</p>
                    <p className="ax-action-sub">
                      {t.sub}
                      <ArrowRight className="ax-action-arrow w-3 h-3" />
                    </p>
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Signups trend */}
          <div className="ax-card ax-card-pad">
            <div className="ax-card-head">
              <div>
                <h2 className="ax-card-title">New clients</h2>
                <p className="ax-card-desc">Cumulative growth · last 30 days</p>
              </div>
              <span className="ax-bignum">+{getNewClientsCount()}</span>
            </div>
            <div style={{ width: '100%', height: 170 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 4, right: 6, left: 6, bottom: 0 }}>
                  <defs>
                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={AX.tooltip} stopOpacity={0.24} />
                      <stop offset="95%" stopColor={AX.tooltip} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" hide />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #E2E8F0', boxShadow: '0 8px 24px -12px rgba(15,23,42,0.35)', fontSize: '12px' }}
                    itemStyle={{ color: AX.tooltip, fontWeight: 700 }}
                    labelStyle={{ color: '#64748B', marginBottom: '4px' }}
                    formatter={(value: any) => [`${value}`, t('admin.newClients')]}
                    labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <Area type="monotone" dataKey="value" stroke={AX.tooltip} strokeWidth={2.5} fill="url(#chartGradient)" fillOpacity={1} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Filters */}
          <div className="ax-card ax-card-pad">
            {/* Advanced Filters */}
            <div className="ax-filter-grid">
              <div className="relative">
                <CountryFilter
                  value={filters.country}
                  onChange={(val) => setFilters({ ...filters, country: val })}
                  placeholder={t('admin.filter.allCountries')}
                />
              </div>
              <div className="relative">
                <DatePicker
                  selected={filters.startDate ? new Date(filters.startDate) : null}
                  onChange={(date: Date | null) => setFilters({ ...filters, startDate: date ? date.toISOString().split('T')[0] ?? '' : '' })}
                  placeholderText={t('admin.filter.startDate')}
                  dateFormat="MMM d, yyyy"
                  className={`w-full ${isRTL ? 'pl-10 pr-4' : 'pl-10 pr-4'} py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm placeholder-slate-400 bg-slate-50 hover:bg-white transition-all text-slate-900 cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`}
                  calendarClassName="custom-calendar"
                  showPopperArrow={false}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  isClearable
                />
                <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none`} />
              </div>
              <div className="relative">
                <DatePicker
                  selected={filters.endDate ? new Date(filters.endDate) : null}
                  onChange={(date: Date | null) => setFilters({ ...filters, endDate: date ? date.toISOString().split('T')[0] ?? '' : '' })}
                  placeholderText={t('admin.filter.endDate')}
                  dateFormat="MMM d, yyyy"
                  className={`w-full ${isRTL ? 'pl-10 pr-4' : 'pl-10 pr-4'} py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm placeholder-slate-400 bg-slate-50 hover:bg-white transition-all text-slate-900 cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`}
                  calendarClassName="custom-calendar"
                  showPopperArrow={false}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  minDate={filters.startDate ? new Date(filters.startDate) : undefined}
                  isClearable
                />
                <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none`} />
              </div>
              <div className="relative">
                <select
                  value={filters.product}
                  onChange={(e) => setFilters({ ...filters, product: e.target.value })}
                  className={`w-full px-4 py-2.5 ${isRTL ? 'pl-10 text-right' : 'pr-10 text-left'} border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-slate-50 hover:bg-white transition-all text-slate-900 appearance-none cursor-pointer`}
                >
                  <option value="">{t('admin.filter.allProducts')}</option>
                  {PRODUCT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none`} />
              </div>
              {!IS_CORP && (<>
              <div className="relative">
                <select
                  value={filters.score}
                  onChange={(e) => setFilters({ ...filters, score: e.target.value })}
                  className={`w-full px-4 py-2.5 ${isRTL ? 'pl-10 text-right' : 'pr-10 text-left'} border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-slate-50 hover:bg-white transition-all text-slate-900 appearance-none cursor-pointer`}
                >
                  <option value="">{t('admin.filter.allScores')}</option>
                  {SCORE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none`} />
              </div>
              <div className="relative">
                <select
                  value={filters.result}
                  onChange={(e) => setFilters({ ...filters, result: e.target.value })}
                  className={`w-full px-4 py-2.5 ${isRTL ? 'pl-10 text-right' : 'pr-10 text-left'} border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-slate-50 hover:bg-white transition-all text-slate-900 appearance-none cursor-pointer`}
                >
                  <option value="">{t('admin.filter.allResults')}</option>
                  {RESULT_COMBINATIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none`} />
              </div>
              </>)}
            </div>

            {/* Brand is locked to this website's domain (corporate vs consulting) —
                each site's admin only shows its own brand's users. */}

            {/* Fast Filters */}
            <div>
              <h3 className="ax-filters-title">{t('admin.quickFilters')}</h3>
              <div className="ax-chips">
                <button
                  onClick={() => toggleFilter('newClients')}
                  className={`ax-chip ${activeFilters.includes('newClients') ? 'is-active' : ''}`}
                >
                  {t('admin.filter.newClients')}
                </button>
                <button
                  onClick={() => toggleFilter('paidClients')}
                  className={`ax-chip ${activeFilters.includes('paidClients') ? 'is-active' : ''}`}
                >
                  {t('admin.filter.paidClients')}
                </button>
                <button
                  onClick={() => toggleFilter('nonPaidClients')}
                  className={`ax-chip ${activeFilters.includes('nonPaidClients') ? 'is-active' : ''}`}
                >
                  {t('admin.filter.nonPaidClients')}
                </button>
                <button
                  onClick={() => toggleFilter('completedKYC')}
                  className={`ax-chip ${activeFilters.includes('completedKYC') ? 'is-active' : ''}`}
                >
                  {t('admin.filter.completedKYC')}
                </button>
                <button
                  onClick={() => toggleFilter('notCompletedKYC')}
                  className={`ax-chip ${activeFilters.includes('notCompletedKYC') ? 'is-active' : ''}`}
                >
                  {t('admin.filter.notCompletedKYC')}
                </button>
                <button
                  onClick={() => toggleFilter('hasCapitalAssessment')}
                  className={`ax-chip ${activeFilters.includes('hasCapitalAssessment') ? 'is-active' : ''}`}
                >
                  Capital Assessment <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => toggleFilter('noCapitalAssessment')}
                  className={`ax-chip ${activeFilters.includes('noCapitalAssessment') ? 'is-active' : ''}`}
                >
                  No Capital Assessment
                </button>
                <button
                  onClick={() => toggleFilter('hasBusinessSetup')}
                  className={`ax-chip ${activeFilters.includes('hasBusinessSetup') ? 'is-active' : ''}`}
                >
                  Business Setup Order
                </button>
                <button
                  onClick={() => {
                    setAppliedFilters(filters);
                    setAppliedActiveFilters(activeFilters);
                  }}
                  className="ax-btn-primary ax-ml-auto"
                >
                  {t('admin.applyFilters')}
                </button>
              </div>
            </div>
          </div>

          {/* Referral Table */}
          <div className="ax-table-card">
            <div className="ax-table-head">
              <h2 className="ax-table-title">{t('admin.referralTable')}</h2>
              <span className="ax-table-count">{filteredUsers.length} client{filteredUsers.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Bulk action bar */}
            {selected.size > 0 && (
              <div className="ax-bulk">
                <span className="ax-bulk-count">{selected.size} selected</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setBulkOpen(true)} className="ax-bulk-btn">
                    <Send className="w-3.5 h-3.5" /> Send notification
                  </button>
                  <button onClick={() => setSelected(new Set())} className="ax-ghost-btn">
                    <X className="w-3.5 h-3.5" /> Clear
                  </button>
                </div>
              </div>
            )}

            {/* Mobile Card View */}
            <div className="block md:hidden">
              {pagedUsers.map((user) => {
                const hasRaising = !!(user as any).raiseCapitalForm && Object.keys((user as any).raiseCapitalForm || {}).length > 0;
                return (
                  <div key={user._id} className="ax-mrow">
                    <div className="flex items-start justify-between" style={{ marginBottom: 12 }}>
                      <div className="flex items-center gap-3">
                        <div className="ax-avatar ax-avatar-sm">
                          {user.firstName?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="ax-name">
                            {user.firstName || 'Unknown'} {user.lastName || ''}
                          </p>
                          <p className="ax-sub">{user.country || 'N/A'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/admin/user/${user._id}`)}
                        className="ax-mlink"
                      >
                        View
                      </button>
                    </div>
                    <div className="space-y-2">
                      <div className="ax-kv">
                        <span className="ax-kv-k">Email:</span>
                        <span className="ax-kv-v truncate ml-2" style={{ maxWidth: 200 }}>{user.email || 'N/A'}</span>
                      </div>
                      <div className="ax-kv">
                        <span className="ax-kv-k">Phone:</span>
                        <span className="ax-kv-v">{user.phone || 'N/A'}</span>
                      </div>
                      <div className="ax-kv">
                        <span className="ax-kv-k">IR Score:</span>
                        <span className="ax-kv-v">{user.irScore ? t('admin.score.yes') : t('admin.score.no')}</span>
                      </div>
                      <div className="ax-kv">
                        <span className="ax-kv-k">Products:</span>
                        <div className="flex gap-1">
                          {user.brand === 'corporate' ? (
                            <span className="ax-pill ax-pill-corp">Corporate</span>
                          ) : (
                            <span className="ax-pill ax-pill-consult">
                              {t('admin.product.consulting')}
                            </span>
                          )}
                          {hasRaising && (
                            <span className="ax-pill ax-pill-raise">
                              {t('admin.product.raising')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="ax-kv">
                        <span className="ax-kv-k">Joined:</span>
                        <span className="ax-kv-v">{formatDate(user.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="ax-table">
                <thead>
                  <tr>
                    <th className="ax-th" style={{ width: 40 }}>
                      <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} className={`w-4 h-4 rounded border-slate-300 ${AX.accent} cursor-pointer`} title="Select all on this page" />
                    </th>
                    <th className={`ax-th ${isRTL ? 'text-right' : 'text-left'}`}>{t('admin.table.name')}</th>
                    <th className={`ax-th ${isRTL ? 'text-right' : 'text-left'}`}>{t('admin.table.email')}</th>
                    <th className={`ax-th ${isRTL ? 'text-right' : 'text-left'}`}>{IS_CORP ? 'Setup' : t('admin.table.irScore')}</th>
                    <th className={`ax-th ${isRTL ? 'text-right' : 'text-left'}`}>{t('admin.table.phone')}</th>
                    <th className={`ax-th ${isRTL ? 'text-right' : 'text-left'}`}>{t('admin.table.products')}</th>
                    <th className={`ax-th ${isRTL ? 'text-right' : 'text-left'}`}>{IS_CORP ? 'Plan' : 'Capital Asmt.'}</th>
                    <th className={`ax-th ${isRTL ? 'text-right' : 'text-left'}`}>{t('admin.table.joined')}</th>
                    <th className={`ax-th ${isRTL ? 'text-left' : 'text-right'}`}>{t('admin.table.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.map((user) => {
                    const hasRaising = !!(user as any).raiseCapitalForm && Object.keys((user as any).raiseCapitalForm || {}).length > 0;

                    return (
                      <tr key={user._id} className={`ax-row ${selected.has(user._id) ? 'is-selected' : ''}`}>
                        <td className="ax-td">
                          <input type="checkbox" checked={selected.has(user._id)} onChange={() => toggleOne(user._id)} className={`w-4 h-4 rounded border-slate-300 ${AX.accent} cursor-pointer`} />
                        </td>
                        <td className="ax-td">
                          <div className="flex items-center gap-3">
                            <div className="ax-avatar">
                              {user.firstName?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="ax-name">
                                {user.firstName || 'Unknown'} {user.lastName || ''}
                              </p>
                              <p className="ax-sub">{user.country || 'N/A'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="ax-td">
                          {user.email || 'N/A'}
                        </td>
                        <td className="ax-td">
                          <p className="ax-strong">
                            {IS_CORP
                              ? (user.businessSetupData?.type ? (user.businessSetupData.type === 'incorporate' ? 'Incorporate' : 'Operate') : '—')
                              : (user.irScore ? 'Yes' : 'No')}
                          </p>
                        </td>
                        <td className="ax-td">
                          {user.phone || 'N/A'}
                        </td>
                        <td className="ax-td">
                          <div className="flex gap-2">
                            {user.brand === 'corporate' ? (
                              <span className="ax-pill ax-pill-corp">Corporate</span>
                            ) : (
                              <span className="ax-pill ax-pill-consult">
                                {t('admin.product.consulting')}
                              </span>
                            )}
                            {hasRaising && (
                              <span className="ax-pill ax-pill-raise">
                                {t('admin.product.raising')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="ax-td">
                          {IS_CORP ? (
                            <span>{user.businessSetupData?.planName || '—'}</span>
                          ) : user.capitalReadiness ? (
                            <span className="ax-pill ax-pill-done">Done</span>
                          ) : (
                            <span className="ax-pill ax-pill-none">—</span>
                          )}
                        </td>
                        <td className="ax-td">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className={`ax-td ${isRTL ? 'text-left' : 'text-right'}`}>
                          <button
                            onClick={() => navigate(`/admin/user/${user._id}`)}
                            className="ax-view-btn"
                          >
                            <User className="w-4 h-4" />
                            {t('admin.table.view')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && (
              <div className="ax-empty">
                <div className="ax-empty-icon">
                  <Users className="w-9 h-9" />
                </div>
                <p className="ax-strong" style={{ marginBottom: 6 }}>No clients found</p>
                <p className="ax-sub">Try adjusting your search or filter criteria</p>
              </div>
            )}

            {/* Pagination */}
            {filteredUsers.length > pageSize && (
              <div className="ax-pager">
                <span className="ax-pager-info">
                  Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredUsers.length)} of {filteredUsers.length}
                </span>
                <div className="ax-pager-btns">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    className="ax-pager-btn"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <span className="ax-pager-page">Page {currentPage} / {totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="ax-pager-btn"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Bulk notification modal */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }} onClick={() => !bulkBusy && setBulkOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2"><Send className={`w-4 h-4 ${AX.text}`} /> Notify {selected.size} client{selected.size !== 1 ? 's' : ''}</h3>
              <button onClick={() => setBulkOpen(false)} disabled={bulkBusy} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5">Title</label>
                <input value={bulkTitle} onChange={e => setBulkTitle(e.target.value)} placeholder="e.g. Important update" className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5">Message</label>
                <textarea value={bulkMsg} onChange={e => setBulkMsg(e.target.value)} rows={4} placeholder="Write your message…" className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-y" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button onClick={() => setBulkOpen(false)} disabled={bulkBusy} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={sendBulkNotification} disabled={bulkBusy} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${AX.solidBtn} text-white text-sm font-bold disabled:opacity-60`}>
                {bulkBusy ? 'Sending…' : <>Send <Send className="w-3.5 h-3.5" /></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
